import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format, parseISO, isValid, getHours } from 'date-fns';
import { Clock, ChevronDown, ChevronUp, Filter } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Timezone, convertTimezone, formatDateInTimezone, timezoneOptions, getTimezoneAbbreviation } from '../../utils/timezoneUtils';

interface HourlyDistributionAnalyticsProps {
  incidents: any[];
  catalogTasks: any[];
  dateRange?: {
    from?: Date;
    to?: Date;
  };
}

interface HourlyData {
  hour: string;
  hourNumber: number;
  incidents: number;
  tasks: number;
  total: number;
}

interface TicketData {
  number: string;
  description: string;
  openedDateTime: string;
  type: 'incident' | 'task';
  priority?: string;
  sortTime: Date;
}

export function HourlyDistributionAnalytics({ incidents, catalogTasks, dateRange }: HourlyDistributionAnalyticsProps) {
  const [selectedHour, setSelectedHour] = useState<string | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [ticketTypeFilter, setTicketTypeFilter] = useState<string>('all');
  const [timezone, setTimezone] = useState<Timezone>('PST');

  const { hourlyData, ticketsByHour, allPriorities } = useMemo(() => {
    const safeIncidents = Array.isArray(incidents) ? incidents.filter(Boolean) : [];
    const safeCatalogTasks = Array.isArray(catalogTasks) ? catalogTasks.filter(Boolean) : [];
    
    console.log("HourlyDistributionAnalytics - Processing data:", {
      incidents: safeIncidents.length,
      catalogTasks: safeCatalogTasks.length,
      timezone,
      dateRange: dateRange ? `${dateRange.from} to ${dateRange.to}` : 'No date range'
    });

    // Initialize hourly buckets (0-23 hours)
    const hourlyBuckets: HourlyData[] = Array.from({ length: 24 }, (_, hour) => ({
      hour: `${hour.toString().padStart(2, '0')}:00`,
      hourNumber: hour,
      incidents: 0,
      tasks: 0,
      total: 0
    }));

    // Initialize tickets by hour tracking
    const ticketsTracker: Record<string, TicketData[]> = {};
    for (let i = 0; i < 24; i++) {
      const hourKey = `${i.toString().padStart(2, '0')}:00`;
      ticketsTracker[hourKey] = [];
    }

    // Collect all unique priorities
    const priorities = new Set<string>();

    // Helper function to process tickets
    const processTickets = (tickets: any[], isIncident: boolean) => {
      let processedCount = 0;
      let invalidDateCount = 0;

      tickets.forEach(ticket => {
        const dateField = ticket['Opened'] || ticket['opened'] || ticket['opened_at'] || 
                          ticket['created'] || ticket['created_at'] || ticket['Created'] || ticket['Created_at'];
        if (!dateField) return;

        try {
          // Convert from PST to selected timezone
          const convertedDate = convertTimezone(dateField, 'PST', timezone);
          
          if (!convertedDate || !isValid(convertedDate)) {
            invalidDateCount++;
            return;
          }

          // Check if date is within range if specified
          if (dateRange && dateRange.from && dateRange.to) {
            if (convertedDate < dateRange.from || convertedDate > dateRange.to) return;
          }

          const ticketPriority = ticket['priority'] || ticket['Priority'] || '';
          if (ticketPriority) {
            priorities.add(ticketPriority);
          }

          // Apply filters
          if (priorityFilter !== 'all' && ticketPriority !== priorityFilter) {
            return;
          }

          if (ticketTypeFilter === 'incidents' && !isIncident) {
            return;
          }

          if (ticketTypeFilter === 'tasks' && isIncident) {
            return;
          }

          const hour = getHours(convertedDate);
          const hourKey = `${hour.toString().padStart(2, '0')}:00`;

          processedCount++;

          // Update counts
          if (isIncident) {
            hourlyBuckets[hour].incidents++;
          } else {
            hourlyBuckets[hour].tasks++;
          }
          hourlyBuckets[hour].total++;

          // Add ticket details with converted time
          const ticketData: TicketData = {
            number: ticket['number'] || ticket['Number'] || '',
            description: ticket['short_description'] || ticket['description'] || ticket['Short description'] || '',
            openedDateTime: formatDateInTimezone(dateField, timezone, 'MMM d, yyyy HH:mm'),
            type: (isIncident ? 'incident' : 'task') as 'incident' | 'task',
            priority: ticketPriority || undefined,
            sortTime: convertedDate
          };
          ticketsTracker[hourKey].push(ticketData);
        } catch (e) {
          console.error("Error processing ticket date:", e, dateField);
        }
      });

      console.log(`Processed ${processedCount} ${isIncident ? 'incidents' : 'tasks'}, ${invalidDateCount} had invalid dates`);
    };

    // Process incidents and catalog tasks
    processTickets(safeIncidents, true);
    processTickets(safeCatalogTasks, false);

    // Sort tickets in each hour by date/time (ascending order)
    Object.keys(ticketsTracker).forEach(hour => {
      ticketsTracker[hour].sort((a, b) => a.sortTime.getTime() - b.sortTime.getTime());
    });

    console.log("HourlyDistributionAnalytics - Final results:", {
      totalTickets: hourlyBuckets.reduce((sum, bucket) => sum + bucket.total, 0),
      hourlyBuckets
    });

    return { hourlyData: hourlyBuckets, ticketsByHour: ticketsTracker, allPriorities: Array.from(priorities) };
  }, [incidents, catalogTasks, dateRange, priorityFilter, ticketTypeFilter, timezone]);

  // Handle bar click
  const handleBarClick = (data: any) => {
    if (data && data.activeLabel) {
      const clickedHour = data.activeLabel;
      setSelectedHour(clickedHour);
      setIsDetailsOpen(true);
    }
  };

  // Get tickets for selected hour
  const selectedHourTickets = selectedHour ? ticketsByHour[selectedHour] || [] : [];

  // Calculate total tickets
  const totalTickets = hourlyData.reduce((sum, bucket) => sum + bucket.total, 0);

  // If no data available, show message
  if (totalTickets === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>24-Hour Distribution Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">No ticket data available</p>
            <p className="text-sm">
              {dateRange && dateRange.from && dateRange.to 
                ? `No tickets found for the selected date range (${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')})`
                : 'No tickets found in the current dataset'
              }
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>24-Hour Distribution Analysis</span>
          <div className="flex items-center gap-3">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={ticketTypeFilter} onValueChange={setTicketTypeFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Ticket Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tickets</SelectItem>
                <SelectItem value="incidents">Incidents Only</SelectItem>
                <SelectItem value="tasks">Tasks Only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                {allPriorities.sort().map(priority => (
                  <SelectItem key={priority} value={priority}>
                    P{priority}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardTitle>
        
        {/* Timezone Controls */}
        <div className="mt-4 p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium mb-2">Current Timezone Display</h4>
              <RadioGroup 
                value={timezone} 
                onValueChange={(value) => setTimezone(value as Timezone)}
                className="flex flex-row gap-4"
              >
                {timezoneOptions.map((tz) => (
                  <div key={tz.value} className="flex items-center space-x-2">
                    <RadioGroupItem value={tz.value} id={tz.value} />
                    <Label htmlFor={tz.value} className="cursor-pointer text-sm">
                      {tz.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              <p className="text-xs text-muted-foreground mt-2">
                Source data is in PST/PDT format
              </p>
             
            </div>
            <div className="text-right">
              {/* <p className="text-sm text-muted-foreground">
                Current timezone: <strong>{getTimezoneAbbreviation(timezone)}</strong>
              </p> */}
              {/* <p className="text-xs text-muted-foreground">
                Source data is in PST/PDT format
              </p> */}
            </div>
          </div>
        </div>
        
        <p className="text-sm text-muted-foreground mt-2">
          Hourly ticket distribution across the entire time period • {totalTickets} total tickets
        </p>
        <p className="text-sm text-muted-foreground">Click on any bar to see detailed ticket list for that hour</p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart 
            data={hourlyData}
            onClick={handleBarClick}
            style={{ cursor: 'pointer' }}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="hour" 
              interval={0}
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis />
            <Tooltip 
              formatter={(value, name) => [value, name === 'incidents' ? 'Incidents' : 'Tasks']}
            />
            <Bar 
              dataKey="incidents" 
              fill="#ef4444" 
              name="incidents"
            />
            <Bar 
              dataKey="tasks" 
              fill="#3b82f6" 
              name="tasks"
            />
          </BarChart>
        </ResponsiveContainer>
        
        {/* Ticket Details Section */}
        {selectedHour && (
          <div className="mt-6">
            <Collapsible open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span className="font-medium">
                    {selectedHour} Tickets ({selectedHourTickets.length})
                  </span>
                </div>
                {isDetailsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-3">
                {selectedHourTickets.length > 0 ? (
                  <div className="space-y-2">
                    {selectedHourTickets.map((ticket, index) => (
                      <div key={index} className="p-3 border rounded-lg bg-background">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge 
                                variant={ticket.type === 'incident' ? 'destructive' : 'default'}
                                className={ticket.type === 'incident' ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600 text-white'}
                              >
                                {ticket.type === 'incident' ? 'INC' : 'TASK'}
                              </Badge>
                              <span className="font-mono text-sm font-medium">{ticket.number}</span>
                              {ticket.priority && (
                                <Badge variant="outline" className="text-xs">
                                  P{ticket.priority}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mb-1">{ticket.description}</p>
                            <p className="text-xs text-muted-foreground">Opened: {ticket.openedDateTime}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    No tickets found for {selectedHour}
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
      </CardContent>
    </Card>
  );
} 